import { useMatchUserList } from "@/api/services/admin/match";
import { useEnum } from "@/api/services/global/enum";
import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { findLabelByValue } from "@/utils/object";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { truncateWithEllipsis } from "@/utils/string";
import { InnerBox } from "@/components/elements/InnerBox";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { TGetMatchUserListDto } from "@/api/services/admin/match/dtos/get-match-user-list.dto";
import { DATE_FORMAT } from "@/constants/datetime";
import dayjs from "dayjs";
import { PillTabs } from "@/components/elements/PillTabs";

// MatchUserTable.tsx
interface MatchUserTableProps {
  matchName: string;
  disabled?: boolean;
}

export const MatchUserTable: FC<MatchUserTableProps> = ({ matchName, disabled }) => {
  const { data: userActiveStatuses } = useEnum("EUserActiveStatus");
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMatchUser = (searchParams.get("tab") || "user") === "user";
  const { data: responseMatchUserList, isLoading } = useMatchUserList(id, isMatchUser ? 1 : 0);
  const companyUserListData: TGetMatchUserListDto[] = responseMatchUserList?.data ?? [];

  const columns = useMemo<MRT_ColumnDef<TGetMatchUserListDto>[]>(() => {
    const rawColumns: MRT_ColumnDef<TGetMatchUserListDto>[] = [
      {
        header: "ユーザー名",
        id: "fullName",
        Cell: ({ row }) => (row.original.fullName ? truncateWithEllipsis(row.original.fullName, 30) : EMPTY_STR.TEXT),
        size: 102,
      },
      {
        header: "メールアドレス",
        id: "email",
        accessorKey: "email",
        Cell: ({ row }) => (row.original.email ? truncateWithEllipsis(row.original.email, 18) : EMPTY_STR.TEXT),
        size: 162,
      },
      {
        header: "所属",
        Cell: ({ row }) => (row.original.isMatchUser && matchName ? truncateWithEllipsis("顧客ユーザー", 40) : "irohana担当者"),
        size: 116,
      },
      {
        header: "ステータス",
        Cell: ({ row }) => (row.original.activeStatus ? findLabelByValue(userActiveStatuses, row.original.activeStatus) : EMPTY_STR.TEXT),
        size: 102,
      },
      {
        header: "登録日",
        Cell: ({ row }) => {
          const date = dayjs(row.original.createdAt);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        size: 106,
      },
      {
        header: "最終ログイン",
        Cell: ({ row }) => {
          const date = dayjs(row.original.lastLoginTime);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        size: 116,
      },
    ];

    return rawColumns.map(
      (col) =>
        ({
          ...col,
          muiTableHeadCellProps: {
            sx: {
              maxWidth: col.size,
              fontSize: "14px !important",
            },
          },
          muiTableBodyCellProps: {
            sx: {
              maxWidth: col.size,
              fontSize: "14px !important",
            },
          },
        }) as MRT_ColumnDef<TGetMatchUserListDto>,
    );
  }, [userActiveStatuses, matchName]);

  const { table } = useTable({
    columns,
    rows: companyUserListData ?? [],
  });

  const handleChangeTab = (value: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value ? "user" : "irohana");
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  return (
    <InnerBox padding="24px">
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="sub16Bold">
              {isMatchUser ? "顧客ユーザーの" : "irohana担当者"}一覧 ({companyUserListData?.length ?? 0})
            </Typography>
            <PillTabs
              options={[
                { label: "顧客ユーザー", value: true },
                { label: "irohana担当者", value: false },
              ]}
              value={isMatchUser}
              onChange={handleChangeTab}
              disabled={disabled}
            />
          </Stack>
        </Stack>
        <Table table={table} isLoading={isLoading} />
        <NewPagination totalPage={responseMatchUserList?.pagination?.totalPage} />
      </Stack>
    </InnerBox>
  );
};
